import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { ResponseErrorsHandlerService } from "../services/response-errors-handler.service";
import { JwtService } from "../services/jwt.service";
import { TokenDataProviderService } from "../services/token-data-provider.service";
import { AuthApiModel } from "../../api/models/auth.api.model";
import { Router } from "@angular/router";

@Injectable()
export class JwtHttpInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private reh: ResponseErrorsHandlerService,
    private jwt: JwtService,
    private ts: TokenDataProviderService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isRefreshRequest = this.isRefreshRequest(request);

    return next.handle(this.addToken(request, this.jwt.token)).pipe(catchError(error => {
      if (!isPlatformBrowser(this.platformId)) {
        return throwError(() => error);
      }
      if (
        error instanceof HttpErrorResponse
        && !isRefreshRequest
        && error.status === 401
        && this.jwt.hasRemember()
      ) {
        return this.handle401Error(request, next, error);
      }

      if (
        error instanceof HttpErrorResponse
        && (
          (!this.jwt.hasRemember() && error.status === 401)
          || (isRefreshRequest && (error.status === 401 || error.status === 400))
        )
      ) {
        this.jwt.clearRemember();
        this.ts.logout();
        this.router.navigate(['/']);
      }

      this.reh.handleServerErrors(error, request.method);
      return throwError(error);
    }));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.jwt.refreshJwtToken().pipe(
        switchMap((successResponse: AuthApiModel) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(successResponse.token);
          return next.handle(this.addToken(request, successResponse.token));
        }), catchError((err: any) => {
          this.isRefreshing = false;
          if (err.status === 401 || err.status === 400) {
            this.jwt.clearRemember();
            this.ts.logout();
            this.router.navigate(['/']);
          }
          return throwError(err);
        }));
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(jwt => {
          return next.handle(this.addToken(request, jwt));
        }));
    }
  }


  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (this.isRefreshRequest(request)) {
      return request;
    }

    if (!token) {
      return request;
    }

    return request.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  private isRefreshRequest(request: HttpRequest<any>): boolean {
    return request.url.includes('/api/token/refresh');
  }
}
